import React, { useRef, useEffect, useState } from 'react';
import { motion, useInView, useAnimation } from 'framer-motion';
import './body.css';

// Component for animated numbers
const AnimatedNumber = ({ from = 0, to, duration = 2 }) => {
  const [count, setCount] = useState(from);
  const controls = useAnimation();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.5 });

  useEffect(() => {
    if (isInView) {
      let startTimestamp;
      const step = (timestamp) => {
        if (!startTimestamp) startTimestamp = timestamp;
        const progress = Math.min((timestamp - startTimestamp) / (duration * 1000), 1);
        const currentCount = Math.floor(progress * (to - from) + from);
        setCount(currentCount);
        if (progress < 1) {
          window.requestAnimationFrame(step);
        }
      };
      window.requestAnimationFrame(step);
    }
  }, [isInView, to, from, duration]);

  return <span ref={ref}>{count}+</span>;
};

function Body() {
  // Refs and inView checks for section animations
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.3 });
  const controls = useAnimation();

  const statsRef = useRef(null);
  const statsInView = useInView(statsRef, { once: true, amount: 0.5 });

  useEffect(() => {
    if (isInView) {
      controls.start("visible");
    }
  }, [isInView, controls]);

  // Data
  const services = [
    {
      title: "Cloud Architecture",
      description: "Scalable, secure, and resilient cloud solutions built for future growth.",
      icon: "☁️"
    },
    {
      title: "AI & Machine Learning",
      description: "Intelligent systems that learn from your data and automate complex processes.",
      icon: "🤖"
    },
    {
      title: "Cybersecurity",
      description: "Proactive protection for your digital assets and user data.",
      icon: "🔒"
    },
    {
      title: "Web Development",
      description: "Modern, responsive websites and web applications that drive results.",
      icon: "💻"
    },
    {
      title: "Mobile Apps",
      description: "Native and cross-platform mobile applications for iOS and Android.",
      icon: "📱"
    },
    {
      title: "IT Consulting",
      description: "Strategic guidance to optimize your technology infrastructure and processes.",
      icon: "📊"
    },
  ];

  const clients = [
    { name: "TechCorp", logo: "🏢" },
    { name: "InnovateLabs", logo: "🔬" },
    { name: "GlobalSoft", logo: "🌐" },
    { name: "DataSystems", logo: "📊" },
    { name: "SecureNet", logo: "🛡️" },
  ];

  const testimonials = [
    {
      quote: "LEARNEX transformed our outdated systems into a modern, efficient infrastructure that has increased our productivity by 40%.",
      author: "Sarah Johnson",
      role: "CTO, TechCorp"
    },
    {
      quote: "Their AI solutions helped us unlock insights from our data that we didn't even know were possible. Truly transformative.",
      author: "Michael Chen",
      role: "Data Director, InnovateLabs"
    },
    {
      quote: "The cybersecurity measures implemented by LEARNEX have given us peace of mind and protected us from multiple threats.",
      author: "Priya Sharma",
      role: "Security Officer, SecureNet"
    }
  ];

  return (
    <div className="home-container">
      {/* ===== HERO SECTION ===== */}
      <section className="hero-section">
        <div className="hero-bg"></div>
        <div className="hero-content-container">
          <motion.div 
            className="hero-content"
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <div className="hero-badge">
              <span>Innovating Since 2021</span>
            </div>
            <motion.h1 
              className="hero-title"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5, duration: 0.8 }}
            >
              Transforming Businesses Through
              <span className="gradient-text"> Intelligent Technology</span>
            </motion.h1>
            <motion.p 
              className="hero-subtitle"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.7, duration: 0.8 }}
            >
              LEARNEX delivers cutting-edge IT solutions that drive growth, efficiency, 
              and competitive advantage for businesses of all sizes.
            </motion.p>
            <motion.div 
              className="hero-cta"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.9, duration: 0.8 }}
            >
              <motion.button 
                className="btn btn-primary"
                whileHover={{ scale: 1.05, boxShadow: "0 10px 25px rgba(37, 99, 235, 0.3)" }}
                whileTap={{ scale: 0.95 }}
              >
                Start Your Project
              </motion.button>
              <motion.button 
                className="btn btn-secondary"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                View Case Studies
              </motion.button>
            </motion.div>
          </motion.div>
          
          <motion.div 
            className="hero-visual"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.5, duration: 0.8 }}
          >
            <div className="tech-graphic">
              <div className="floating-element element-1">AI</div>
              <div className="floating-element element-2">Cloud</div>
              <div className="floating-element element-3">Data</div>
              <div className="floating-element element-4">IoT</div>
              <div className="main-circle">
                <div className="inner-circle">IT Solutions</div>
              </div>
            </div>
          </motion.div>
        </div>
        
        <motion.div 
          className="scrolldown-indicator"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.5, duration: 1 }}
        >
          <span>Scroll to explore</span>
          <div className="scroll-line"></div>
        </motion.div>
      </section>

      {/* ===== CLIENT LOGO BAR ===== */}
      <motion.section 
        className="client-bar"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true, amount: 0.8 }}
        transition={{ duration: 0.8 }}
      >
        <p>Trusted by industry leaders</p>
        <div className="client-logos">
          {clients.map((client, index) => (
            <motion.div 
              key={index}
              className="client-logo"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.8 }}
              transition={{ delay: index * 0.1 }}
              whileHover={{ scale: 1.1, y: -5 }}
            >
              <div className="client-logo-icon">{client.logo}</div>
              <span>{client.name}</span>
            </motion.div>
          ))}
        </div>
      </motion.section>

      {/* ===== SERVICES SECTION ===== */}
      <section className="services-section" ref={ref}>
        <div className="section-header">
          <motion.h2
            variants={{
              hidden: { opacity: 0, y: 50 },
              visible: { opacity: 1, y: 0 }
            }}
            initial="hidden"
            animate={controls}
            transition={{ duration: 0.6 }}
          >
            Our Core Services
          </motion.h2>
          <motion.p
            variants={{
              hidden: { opacity: 0, y: 30 },
              visible: { opacity: 1, y: 0 }
            }}
            initial="hidden"
            animate={controls}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="section-subtitle"
          >
            Comprehensive IT solutions tailored to your business needs
          </motion.p>
        </div>
        <div className="services-grid">
          {services.map((service, index) => (
            <motion.div
              key={index}
              className="service-card"
              variants={{
                hidden: { opacity: 0, y: 50 },
                visible: { opacity: 1, y: 0 }
              }}
              initial="hidden"
              animate={controls}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              whileHover={{ y: -10, transition: { duration: 0.2 } }}
            >
              <div className="service-icon">{service.icon}</div>
              <h3>{service.title}</h3>
              <p>{service.description}</p>
              <button className="service-link">Learn more →</button>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ===== STATS SECTION ===== */}
      <section className="stats-section" ref={statsRef}>
        <div className="stats-container">
          <div className="section-header">
            <h2>Our Impact in Numbers</h2>
            <p className="section-subtitle">Delivering measurable results for our clients</p>
          </div>
          <div className="stats-grid">
            <motion.div 
              className="stat"
              initial={{ opacity: 0 }}
              animate={statsInView ? { opacity: 1 } : {}}
              transition={{ duration: 0.6 }}
            >
              <h3><AnimatedNumber from={0} to={250} /></h3>
              <p>Projects Delivered</p>
            </motion.div>
            <motion.div 
              className="stat"
              initial={{ opacity: 0 }}
              animate={statsInView ? { opacity: 1 } : {}}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <h3><AnimatedNumber from={0} to={99} />%</h3>
              <p>Client Satisfaction</p>
            </motion.div>
            <motion.div 
              className="stat"
              initial={{ opacity: 0 }}
              animate={statsInView ? { opacity: 1 } : {}}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              <h3><AnimatedNumber from={0} to={50} /></h3>
              <p>Expert Team Members</p>
            </motion.div>
            <motion.div 
              className="stat"
              initial={{ opacity: 0 }}
              animate={statsInView ? { opacity: 1 } : {}}
              transition={{ duration: 0.6, delay: 0.6 }}
            >
              <h3><AnimatedNumber from={0} to={15} /></h3>
              <p>Industries Served</p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ===== TESTIMONIALS SECTION ===== */}
      <section className="testimonials-section">
        <div className="section-header">
          <h2>What Our Clients Say</h2>
          <p className="section-subtitle">Hear from businesses we've transformed</p>
        </div>
        <div className="testimonials-grid">
          {testimonials.map((testimonial, index) => (
            <motion.div 
              key={index}
              className="testimonial-card"
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.5 }}
              transition={{ duration: 0.6, delay: index * 0.2 }}
            >
              <div className="testimonial-quote">"</div>
              <p>{testimonial.quote}</p>
              <div className="testimonial-author">
                <h4>{testimonial.author}</h4>
                <span>{testimonial.role}</span>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ===== FINAL CTA SECTION ===== */}
      <motion.section 
        className="cta-section"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true, amount: 0.5 }}
        transition={{ duration: 1 }}
      >
        <div className="cta-content">
          <h2>Ready to Transform Your Business?</h2>
          <p>Let's discuss how LEARNEX can help you achieve your technology goals and drive growth.</p>
          <div className="cta-buttons">
            <motion.button 
              className="btn btn-primary btn-large"
              whileHover={{ scale: 1.05, boxShadow: "0 15px 30px rgba(37, 99, 235, 0.3)" }}
              whileTap={{ scale: 0.98 }}
            >
              Get a Free Consultation
            </motion.button>
            <motion.button 
              className="btn btn-secondary btn-large"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.98 }}
            >
              View Our Services
            </motion.button>
          </div>
        </div>
      </motion.section>
    </div>
  );
}

export default Body;